import { getLocales } from 'expo-localization';

import { useEarlyIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useLDContext } from 'state/launchdarkly';
import { staticLDFlags } from 'state/launchdarkly/launchdarkly-core';
import { AppTakeover } from 'state/launchdarkly/variations';
import { region } from 'utils/environment';
import { Region } from 'utils/environment/types';

import { spongebob } from './spongebob/spongebob';
import { TakeoverMenuSectionConfig, TakeoverPdpConfig } from './types';

// TODO define an official real type if we do another takeover
export type AppTakeoverType = typeof spongebob;

// add additional takeovers here
const appTakeoverConfigs: Record<string, AppTakeoverType> = {
  spongebob,
};

const INACTIVE = Object.freeze({ isTakeoverActive: false as const });

/** get a takeover if one is active per LD date ranges */
export const useAppTakeover = (): Partial<
  AppTakeoverType & { isTakeoverActive: boolean; name: string }
> => {
  const ldContext = useLDContext();
  const isDesktop = useEarlyIsDesktopWebBreakpoint();

  if (isDesktop) {
    // disable on desktop, designs assume mobile as is most of our users
    return INACTIVE;
  }

  if (getIsDisabledLanguage()) {
    return INACTIVE;
  }

  // read from static flags if LD context is not initialized yet since this is used outside of provider in startup animation
  const appTakeover = (ldContext && ldContext.isInitialized
    ? ldContext.flags[LaunchDarklyFlag.APP_TAKEOVER]
    : staticLDFlags.current[LaunchDarklyFlag.APP_TAKEOVER]) as AppTakeover;

  if (appTakeover && appTakeover.length) {
    const now = new Date();
    const activeTakeover = appTakeover.find(takeover => {
      const start = new Date(takeover.startDate);
      const end = new Date(takeover.endDate);
      return now >= start && now <= end;
    });
    if (activeTakeover) {
      return {
        ...appTakeoverConfigs[activeTakeover.name],
        isTakeoverActive: true,
        name: activeTakeover.name,
      };
    }
  }
  return INACTIVE;
};

/** return a SHP config if takeover is active for the passed home page */
export const useAppTakeoverSimpleHomePage = (
  homePageId: string
): Partial<AppTakeoverType['homePage'] & { isTakeoverActive: boolean; fontFamily: string }> => {
  const appTakeover = useAppTakeover();

  if (!homePageId) {
    return { isTakeoverActive: false };
  }

  if (appTakeover?.homePage?.targetSimpleHomePageIds?.includes(homePageId)) {
    return {
      ...appTakeover?.homePage,
      isTakeoverActive: true,
      fontFamily: appTakeover?.fontFamily,
    };
  }
  return INACTIVE;
};

type MenuSectionResult = TakeoverMenuSectionConfig & {
  isTakeoverActive: boolean;
};

/** returns a menu section config if takeover is active for the passed section */
export const useAppTakeoverMenuSection = (sectionId: string): MenuSectionResult => {
  const appTakeover = useAppTakeover();

  if (!sectionId) {
    return INACTIVE;
  }

  const takeoverSectionConfig = appTakeover?.menu?.targetSectionIds[sectionId];
  if (takeoverSectionConfig && Object.keys(takeoverSectionConfig).length > 0) {
    return { ...takeoverSectionConfig, isTakeoverActive: true };
  }

  return INACTIVE;
};

export type PdpResult = Partial<
  AppTakeoverType['pdp'] & { isTakeoverActive: boolean; fontFamily: string } & TakeoverPdpConfig
>;

/** returns a PDP config if takeover is active for the menu item */
export const useAppTakeoverPdp = (itemId: string): PdpResult => {
  const appTakeover = useAppTakeover();

  if (!itemId) {
    return INACTIVE;
  }

  const itemConfig = appTakeover?.pdp?.targetMenuItemIds?.[itemId];
  if (itemConfig) {
    return {
      ...itemConfig,
      ...appTakeover?.pdp,
      isTakeoverActive: true,
      fontFamily: appTakeover?.fontFamily,
    };
  }

  return INACTIVE;
};

const getIsDisabledLanguage = () => {
  if (region === Region.US) {
    return false;
  }

  try {
    const deviceLanguage = getLocales()?.[0]?.languageCode?.toLowerCase();
    if (deviceLanguage?.includes('fr')) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
};
